/*
  assets/drg-nature-zone.js — NATURE-ZONE-75-SCOPED-RUNTIME
  Owner: existing Dr.G Nature Quest free-zone adapter.

  Scope:
  - [data-lg-nature-root]
  - the nearest matching [data-drg-nature-zone-adapter] payload
  - Nature-only local history keys

  Boundary:
  - No canonical City Mission history writes.
  - No GFun, checkout, paid delivery, customer metafield, API, PDF or formal-review execution.
  - Free-zone safety is evaluated inside each Nature root, never from unrelated sitewide assets.
*/
(function () {
  'use strict';

  var VERSION = 'NATURE-ZONE-75-SCOPED-RUNTIME';
  if (window.__DRG_NATURE_ZONE_ADAPTER__ === VERSION) return;
  window.__DRG_NATURE_ZONE_ADAPTER__ = VERSION;

  var ROOT_SELECTOR = '[data-lg-nature-root]';
  var ADAPTER_SELECTOR = '[data-drg-nature-zone-adapter]';
  var ADAPTER_SECTION_SELECTOR = '[data-drg-nature-zone-adapter-section]';
  var NATURE_HISTORY_KEY = 'drg_nature_mission_history_v1';
  var LATEST_KEY = 'drg_nature_mission_latest_v1';
  var DEFAULT_PRO_URL = '/pages/gproject-showcases?market=environment-natural-resources';
  var DEFAULT_SOURCE = 'drg-nature-quest';
  var NATURE_ZONE_ID = 'nature-explore';
  var FREE_MARKET = 'green-city-service';
  var FREE_RELATED_MARKETS = 'land-natural-resources|water-resilience';
  var FREE_RELATED_MARKET_LIST = Object.freeze(['land-natural-resources', 'water-resilience']);
  var PRO_DATA_ATTRIBUTES = Object.freeze([
    'data-lg-island-gfun',
    'data-preview-product-handle',
    'data-preview-canonical-endpoint',
    'data-report-product-handle',
    'data-report-canonical-endpoint',
    'data-review-product-handle',
    'data-review-canonical-endpoint',
    'data-api-family',
    'data-api-trigger-enabled',
    'data-canonical-endpoint',
    'data-contract-state',
    'data-delivery-method',
    'data-delivery-type',
    'data-result-surface',
    'data-requires-gfun',
    'data-gfun-consume-per-call',
    'data-gfun-cost',
    'data-variant-id',
    'data-product-url',
    'data-handoff-url',
    'data-product-handle',
  ]);
  var PRO_CONTEXT_SELECTOR = PRO_DATA_ATTRIBUTES.map(function (name) {
    return '[' + name + ']';
  }).join(',');
  var FREE_ZONE_TARGET_SELECTOR = [
    '[data-lg-nature-trigger]',
    '[data-lg-nature-island]',
    '[data-gfun-generate]',
    '[data-evidence-cart-add]',
    '[data-review-brief-link]',
    '[data-tier-review-link]',
    '[data-island-delivery-tier-card]',
  ].join(',');
  var HIDDEN_PRO_SELECTOR = [
    '[data-island-delivery-tier-card]',
    '[data-gfun-generate]',
    '[data-evidence-cart-add]',
    '[data-tier-gfun-title]',
    '[data-lg-drawer-gfun]',
    '[data-lg-gfun-total]',
    '.lg-nature-card__gfun',
  ].join(',');
  var CHECKOUT_HOOK_SELECTOR = [
    'form[action*="/cart/add"]',
    '[data-cart-add]',
    '[data-checkout]',
    '[data-gfun-checkout]',
    '[data-evidence-cart-add]',
  ].join(',');

  /** @type {Set<DirectoryRoot>} */
  const mountedRoots = new Set();
  var stateByRoot = new WeakMap();

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function query(selector, root) {
    var scope = root || document;
    return scope.querySelector ? scope.querySelector(selector) : null;
  }

  function queryAll(selector, root) {
    var scope = root || document;
    return scope.querySelectorAll
      ? Array.prototype.slice.call(scope.querySelectorAll(selector))
      : [];
  }

  function safeParse(text, fallback) {
    try {
      var parsed = text ? JSON.parse(text) : fallback;
      return parsed == null ? fallback : parsed;
    } catch (_error) {
      return fallback;
    }
  }

  function safeInternalUrl(value, fallback) {
    var raw = clean(value || fallback);
    try {
      var url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return fallback;
      return url.pathname + url.search + url.hash;
    } catch (_error) {
      return fallback;
    }
  }

  function readList(key) {
    try {
      var parsed = safeParse(window.localStorage.getItem(key), []);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  function writeList(key, items) {
    try {
      window.localStorage.setItem(
        key,
        JSON.stringify((Array.isArray(items) ? items : []).slice(0, 80))
      );
      return true;
    } catch (_error) {
      return false;
    }
  }

  function writeObject(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function dispatch(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (_error) {}
  }

  function normalizeIsland(value) {
    return (
      clean(value || 'mountain')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'mountain'
    );
  }

  function normalizeSource(value) {
    var source = clean(value);
    if (
      !source ||
      source === 'drg-museum' ||
      source === 'city-museum' ||
      source === 'legacy-city-museum'
    ) {
      return DEFAULT_SOURCE;
    }
    return source;
  }

  function makeId(prefix, island) {
    var suffix = Date.now() + '-' + Math.random().toString(16).slice(2, 10);
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      suffix = window.crypto.randomUUID();
    }
    return [prefix, normalizeIsland(island), suffix].join('_').replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  function adapterMode(adapter) {
    if (!adapter) return '';
    var section = adapter.closest ? adapter.closest(ADAPTER_SECTION_SELECTOR) : null;
    return clean(
      (adapter.dataset && adapter.dataset.natureMode) ||
        (section && section.dataset && section.dataset.natureMode)
    );
  }

  function nearestAdapter(root) {
    var adapters = queryAll(ADAPTER_SELECTOR);
    if (!adapters.length) return null;
    if (adapters.length === 1) return adapters[0];

    var rootMode = clean(root && root.dataset && root.dataset.natureMode);
    var matching = adapters.filter(function (adapter) {
      return !rootMode || adapterMode(adapter) === rootMode;
    });
    var candidates = matching.length ? matching : adapters;
    var ordered = queryAll(ROOT_SELECTOR + ',' + ADAPTER_SELECTOR);
    var rootIndex = ordered.indexOf(root);

    if (rootIndex === -1) return candidates[0];

    return candidates
      .map(function (adapter) {
        var index = ordered.indexOf(adapter);
        return {
          adapter: adapter,
          distance: index === -1 ? Number.MAX_SAFE_INTEGER : Math.abs(index - rootIndex),
          preceding: index !== -1 && index < rootIndex,
        };
      })
      .sort(function (left, right) {
        if (left.distance !== right.distance) return left.distance - right.distance;
        if (left.preceding !== right.preceding) return left.preceding ? -1 : 1;
        return 0;
      })[0].adapter;
  }

  function getMode(root) {
    var rootMode = clean(root && root.dataset && root.dataset.natureMode);
    if (rootMode) return rootMode;
    return adapterMode(nearestAdapter(root)) || 'free_zone';
  }

  function getBasePayload(root) {
    var adapter = nearestAdapter(root);
    var payloadNode = adapter ? query('[data-drg-nature-zone-payload]', adapter) : null;
    var parsed = payloadNode ? safeParse(payloadNode.textContent, {}) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) parsed = {};

    var payload = Object.assign(
      {
        source: DEFAULT_SOURCE,
        nature_zone_id: NATURE_ZONE_ID,
        market_handle: FREE_MARKET,
        related_market_handles: FREE_RELATED_MARKET_LIST.slice(),
        card_type: 'nature-mission-card',
        mission_line: 'I made this city 0.01% greener.',
        discovered_issue: '我發現一個自然線索',
        badge_name: 'Nature Explorer Badge',
        pro_workspace_url: DEFAULT_PRO_URL,
      },
      parsed,
      {
        source: normalizeSource(parsed.source),
        pro_workspace_url: safeInternalUrl(parsed.pro_workspace_url, DEFAULT_PRO_URL),
      }
    );

    [
      'product_handle',
      'variant_id',
      'gfun_cost',
      'gfun_consume_per_call',
      'api_family',
      'canonical_endpoint',
      'delivery_type',
      'delivery_method',
    ].forEach(function (key) {
      delete payload[key];
    });

    return payload;
  }

  function getActiveContext(root) {
    var activeTrigger =
      query('[data-lg-nature-trigger].is-active', root) ||
      query('[data-lg-nature-trigger][aria-pressed="true"]', root) ||
      query('[data-lg-nature-trigger]', root);
    var activeCard =
      query('[data-lg-nature-island][data-active="true"]', root) ||
      query('[data-lg-nature-island].is-active', root) ||
      query('[data-lg-nature-island]', root);
    var source = activeTrigger || activeCard || root;
    var fallback = activeCard || activeTrigger || root;
    var sourceData = source.dataset || {};
    var fallbackData = fallback.dataset || {};
    var island = normalizeIsland(
      sourceData.lgNatureTrigger || sourceData.island || root.dataset.currentIsland || 'mountain'
    );
    var freeZone = getMode(root) === 'free_zone';

    return {
      island: island,
      title: clean(sourceData.lgIslandTitle || fallbackData.lgIslandTitle) || 'Nature Island',
      mission:
        clean(sourceData.lgIslandMission || fallbackData.lgIslandMission) || 'Nature Mission',
      description: clean(sourceData.lgIslandDescription || fallbackData.lgIslandDescription),
      evidence:
        clean(
          sourceData.lgIslandEvidence ||
            sourceData.lgIslandEvidenceText ||
            fallbackData.lgIslandEvidence ||
            fallbackData.lgIslandEvidenceText ||
            fallbackData.lgLearningEvidence
        ) || '自然線索觀察',
      evidenceType:
        clean(
          sourceData.lgIslandEvidenceType ||
            fallbackData.lgIslandEvidenceType ||
            fallbackData.lgLearningEvidence
        ) || 'nature_learning_evidence',
      evidenceId:
        clean(sourceData.evidenceId || fallbackData.evidenceId) || 'nature-' + island + '-draft',
      market: freeZone
        ? FREE_MARKET
        : clean(sourceData.marketHandle || fallbackData.marketHandle) || FREE_MARKET,
      relatedMarket: freeZone
        ? FREE_RELATED_MARKETS
        : clean(sourceData.relatedMarketHandle || fallbackData.relatedMarketHandle) ||
          FREE_RELATED_MARKETS,
    };
  }

  function buildPayload(root, reason) {
    var base = getBasePayload(root);
    var context = getActiveContext(root);
    var resultId = makeId('nature', context.island);
    var missionLine = clean(base.mission_line) || 'I made this city 0.01% greener.';
    var badgeName = clean(base.badge_name) || 'Nature Explorer Badge';
    var discovered =
      context.evidence ||
      context.description ||
      clean(base.discovered_issue) ||
      '我發現一個自然線索';
    var proUrl = safeInternalUrl(base.pro_workspace_url, DEFAULT_PRO_URL);
    var summary = [missionLine, '我發現：' + discovered, '我的徽章：' + badgeName];

    return Object.assign({}, base, {
      id: resultId,
      result_id: resultId,
      evidence_id: context.evidenceId,
      evidence_type: context.evidenceType,
      source: normalizeSource(base.source),
      compatibility_source: 'drg-city-mission',
      nature_zone_id: clean(base.nature_zone_id) || NATURE_ZONE_ID,
      market_handle: FREE_MARKET,
      original_market_handle: context.market,
      related_market_handles: FREE_RELATED_MARKET_LIST.slice(),
      related_market_handle: context.relatedMarket || FREE_RELATED_MARKETS,
      card_type: 'nature-mission-card',
      type: 'nature_mission_card',
      file_type: 'nature_mission_card',
      workflow: 'drg_nature_zone_free_task',
      result_surface: 'panel',
      title: 'Nature Mission Card',
      name: 'Nature Mission Card',
      island: context.island,
      island_handle: 'nature-' + context.island,
      direction_value: 'greener',
      mission_direction: 'greener',
      direction_label: '更綠色',
      mission_line: missionLine,
      share_line: missionLine,
      discovered_issue: discovered,
      selected_issue: discovered,
      selected_signal: context.title + ' · ' + context.mission,
      badge_title: '自然探索觀察員',
      badge_name: badgeName,
      certificate_title: 'LittleG Nature Mission Certificate',
      issuer: 'LittleG 小量博士',
      challenge_line: '我完成了一個 Nature Explore Zone 任務，下一位輪到你。',
      pro_workspace_url: proUrl,
      professional_route: proUrl,
      review_url: proUrl,
      summary: summary.join(' '),
      share_text: summary.join('\n'),
      created_at: new Date().toISOString(),
      status: 'ready',
      result_status: 'ready',
      local_adapter_reason: clean(reason) || 'nature-zone-adapter',
      disclaimer: 'This is a free Nature Quest task. It is not a formal professional conclusion.',
    });
  }

  function upsert(list, item) {
    var key = item.result_id || item.id || item.evidence_id;
    return [item].concat(
      (Array.isArray(list) ? list : []).filter(function (existing) {
        if (!existing || typeof existing !== 'object') return false;
        return (existing.result_id || existing.id || existing.evidence_id) !== key;
      })
    );
  }

  function persistPayload(payload) {
    var storedPayload = Object.assign({}, payload);
    var savedNature = writeList(
      NATURE_HISTORY_KEY,
      upsert(readList(NATURE_HISTORY_KEY), storedPayload)
    );
    var savedLatest = writeObject(LATEST_KEY, storedPayload);

    return Object.assign({}, storedPayload, {
      local_save_ok: savedNature && savedLatest,
      local_storage_owner: 'drg-nature-zone',
    });
  }

  function removeAttributes(node, attributes) {
    if (!node) return;
    attributes.forEach(function (attribute) {
      node.removeAttribute(attribute);
    });
  }

  function cleanFreeZoneRuntimeContext(root) {
    if (getMode(root) !== 'free_zone') return;

    root.setAttribute('data-primary-market-handle', FREE_MARKET);
    root.setAttribute('data-related-market-handles', FREE_RELATED_MARKETS);
    root.setAttribute('data-market-handle', FREE_MARKET);
    root.setAttribute('data-related-market-handle', FREE_RELATED_MARKETS);
    removeAttributes(root, PRO_DATA_ATTRIBUTES);

    queryAll(FREE_ZONE_TARGET_SELECTOR, root).forEach(function (node) {
      removeAttributes(node, PRO_DATA_ATTRIBUTES);
      if (
        node.matches &&
        (node.matches('[data-lg-nature-trigger]') || node.matches('[data-lg-nature-island]'))
      ) {
        node.setAttribute('data-market-handle', FREE_MARKET);
        node.setAttribute('data-related-market-handle', FREE_RELATED_MARKETS);
      }
    });
  }

  function safetyFallback(root) {
    if (getMode(root) !== 'free_zone') return;

    queryAll(HIDDEN_PRO_SELECTOR, root).forEach(function (node) {
      node.hidden = true;
      node.setAttribute('aria-hidden', 'true');
      if ('disabled' in node) node.disabled = true;
    });
  }

  function publishCard(root, payload) {
    root.__lastDrGNaturePayload = payload;
    root.dataset.drgNatureSaved = payload.local_save_ok ? 'true' : 'false';

    var bridge = query('.lg-nature-quest__bridge', root);
    if (bridge) bridge.setAttribute('data-drg-nature-card-ready', 'true');

    [
      'drg:nature-zone:card-ready',
      'littleg:nature-zone:card-ready',
      'littleg:mini-game:preview-generated',
      'littleg:city-mission:souvenir',
    ].forEach(function (eventName) {
      dispatch(eventName, payload);
    });
  }

  function bindCta(root, state) {
    var cta = query('[data-lg-drawer-cta]', root);
    if (!cta || getMode(root) !== 'free_zone') return;

    cta.textContent = '生成 Nature Mission Card';
    cta.href = '#nature-mission-card';
    cta.dataset.drgNatureBound = VERSION;

    var options = state.controller ? { signal: state.controller.signal } : undefined;
    var clickHandler = function (event) {
      event.preventDefault();
      if (!state.active) return;
      var payload = persistPayload(buildPayload(root, 'cta'));
      publishCard(root, payload);
      publishState(root);
    };

    try {
      cta.addEventListener('click', clickHandler, options);
    } catch (_error) {
      cta.addEventListener('click', clickHandler);
      state.cleanups.push(function () {
        cta.removeEventListener('click', clickHandler);
      });
    }
  }

  function markRoot(root) {
    var mode = getMode(root);
    root.setAttribute('data-nature-zone', NATURE_ZONE_ID);
    root.setAttribute('data-nature-mode', mode);
    root.setAttribute('data-free-zone', mode === 'free_zone' ? 'true' : 'false');
    root.setAttribute('data-market-pro-target', 'environment-natural-resources');
    root.setAttribute('data-card-type', 'nature-mission-card');
    root.setAttribute('data-drg-nature-zone-adapted', 'true');
    root.setAttribute('data-drg-nature-owner', VERSION);
  }

  function hasVisible(root, selector) {
    return queryAll(selector, root).some(function (node) {
      return !node.hidden && node.getAttribute('aria-hidden') !== 'true';
    });
  }

  function hasFreeZoneProductContext(root) {
    return Boolean(PRO_CONTEXT_SELECTOR && query(PRO_CONTEXT_SELECTOR, root));
  }

  function rootState(root) {
    var mode = getMode(root);
    var context = getActiveContext(root);
    var freeZone = mode === 'free_zone';
    var hasCheckout = freeZone && hasVisible(root, CHECKOUT_HOOK_SELECTOR);
    var hasGfun =
      freeZone &&
      hasVisible(root, '[data-lg-gfun-total], [data-lg-drawer-gfun], .lg-nature-card__gfun');
    var hasDelivery = freeZone && hasVisible(root, '[data-island-delivery-tier-card]');
    var hasProductHandle = freeZone && hasFreeZoneProductContext(root);
    var safe = !freeZone || (!hasCheckout && !hasGfun && !hasDelivery && !hasProductHandle);

    return {
      version: VERSION,
      ok: safe,
      mode: mode,
      section_id: clean(root.dataset.sectionId),
      nature_zone_id: NATURE_ZONE_ID,
      market_handle: FREE_MARKET,
      related_market_handles: FREE_RELATED_MARKET_LIST.slice(),
      card_type: 'nature-mission-card',
      active_island: context.island,
      active_evidence_id: context.evidenceId,
      has_checkout: hasCheckout,
      has_gfun: hasGfun,
      has_delivery: hasDelivery,
      has_product_handle: hasProductHandle,
      pro_url: safeInternalUrl(getBasePayload(root).pro_workspace_url, DEFAULT_PRO_URL),
    };
  }

  function updateAggregateState() {
    var states = [];
    mountedRoots.forEach(function (root) {
      if (root.isConnected && root.__drgNatureZoneState) {
        states.push(root.__drgNatureZoneState);
      }
    });

    var allSafe = states.every(function (state) {
      return state.ok;
    });
    var primary = states[0] || {
      version: VERSION,
      ok: true,
      mode: 'unmounted',
      nature_zone_id: NATURE_ZONE_ID,
      market_handle: FREE_MARKET,
      related_market_handles: FREE_RELATED_MARKET_LIST.slice(),
      card_type: 'nature-mission-card',
    };

    window.__DRG_NATURE_ZONE_STATE__ = Object.assign({}, primary, {
      root_count: states.length,
      all_roots_ok: allSafe,
    });
    document.documentElement.setAttribute(
      'data-drg-nature-zone-mode',
      states.length === 1 ? states[0].mode : states.length ? 'multiple' : 'unmounted'
    );
    document.documentElement.setAttribute('data-drg-nature-zone-state', allSafe ? 'ok' : 'check');
  }

  function publishState(root) {
    var state = rootState(root);
    root.__drgNatureZoneState = state;
    root.setAttribute('data-drg-nature-zone-state', state.ok ? 'ok' : 'check');
    updateAggregateState();
    return state;
  }

  function adapt(root) {
    if (!(root instanceof HTMLElement)) return;
    markRoot(root);
    cleanFreeZoneRuntimeContext(root);
    safetyFallback(root);
    publishState(root);
  }

  function mount(root) {
    if (!root || stateByRoot.has(root)) return;

    var state = {
      active: true,
      controller: typeof AbortController === 'function' ? new AbortController() : null,
      cleanups: [],
    };
    stateByRoot.set(root, state);
    mountedRoots.add(root);

    markRoot(root);
    cleanFreeZoneRuntimeContext(root);
    safetyFallback(root);
    bindCta(root, state);
    publishState(root);
  }

  function destroy(root) {
    var state = root ? stateByRoot.get(root) : null;
    if (!state) return;

    state.active = false;
    if (state.controller) state.controller.abort();
    state.cleanups.forEach(function (cleanup) {
      cleanup();
    });

    var cta = query('[data-lg-drawer-cta]', root);
    if (cta) cta.removeAttribute('data-drg-nature-bound');

    mountedRoots.delete(root);
    stateByRoot.delete(root);
    delete root.__drgNatureZoneState;
    delete root.__lastDrGNaturePayload;
    root.removeAttribute('data-drg-nature-zone-adapted');
    root.removeAttribute('data-drg-nature-owner');
    root.removeAttribute('data-drg-nature-zone-state');
    root.removeAttribute('data-drg-nature-saved');
    updateAggregateState();
  }

  function mountAll(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) mount(target);
    queryAll(ROOT_SELECTOR, target).forEach(mount);
  }

  function destroyAll(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) destroy(target);
    queryAll(ROOT_SELECTOR, target).forEach(destroy);
  }

  function refreshAll() {
    mountedRoots.forEach(function (root) {
      if (!root.isConnected) {
        destroy(root);
        return;
      }
      adapt(root);
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target || document);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target || document);
  });

  window.addEventListener('littleg:nature-route-change', function () {
    refreshAll();
    mountedRoots.forEach(function (root) {
      root.__lastDrGNaturePayload = buildPayload(root, 'route-change');
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        mountAll(document);
      },
      { once: true }
    );
  } else {
    mountAll(document);
  }
})();
